import React, { useState, useEffect, useContext } from 'react';
import { useNavigate } from 'react-router-dom';
import { AuthContext } from '../App';
import axios from 'axios';
import { toast } from 'sonner';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import { QRCodeSVG } from 'qrcode.react';

// ============================================================
// CONFIGURACIÓN: Sistema de clases con Zoom
// ============================================================
// Para cambiar entre sistema NUEVO (SDK embebido) y ANTIGUO (link directo):
// - true = SDK embebido para estudiantes/pastor/graduado (SOLO EN DESKTOP)
// - false = Link directo para todos (sistema original)
const USAR_SDK_EMBEBIDO = true; // REACTIVADO - Usar Chrome/Brave en móvil para cámara

// Roles que usarán el SDK embebido (los demás usan link directo)
const ROLES_SDK_EMBEBIDO = ['estudiante', 'pastor', 'graduado'];

// Detectar si es dispositivo móvil (móvil siempre usa link directo para cámara)
const esMobile = () => {
  return /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent) ||
         (window.innerWidth <= 768);
};
// ============================================================

const MiClaseHoyWidget = () => {
  const { API, user } = useContext(AuthContext);
  const navigate = useNavigate();
  const [clasesHoy, setClasesHoy] = useState([]);
  const [eventosHoy, setEventosHoy] = useState([]);
  const [loading, setLoading] = useState(true);
  const [iniciandoClase, setIniciandoClase] = useState({});
  
  // Estado para modal de estudiantes
  const [showEstudiantesModal, setShowEstudiantesModal] = useState(false);
  const [estudiantesInscritos, setEstudiantesInscritos] = useState([]);
  const [loadingEstudiantes, setLoadingEstudiantes] = useState(false);
  const [selectedClase, setSelectedClase] = useState(null);
  // Mapa de estudiantes {id: {nombre, foto_perfil}} para mostrar avatars en cada tarjeta
  const [estudiantesMap, setEstudiantesMap] = useState({});
  
  // Estado para modal de selección de método de entrada (Zoom directo vs SDK)
  const [showMetodoModal, setShowMetodoModal] = useState(false);
  const [claseParaEntrar, setClaseParaEntrar] = useState(null);

  // Estado para confirmación de link de emergencia
  const [showEmergenciaConfirm, setShowEmergenciaConfirm] = useState(false);
  const [claseEmergencia, setClaseEmergencia] = useState(null);
  const [enviandoEmergencia, setEnviandoEmergencia] = useState(false);

  // Aviso "No hay clase hoy" (solo admin)
  const [showAvisoModal, setShowAvisoModal] = useState(false);
  const [avisoClase, setAvisoClase] = useState(null);
  const [avisoPreset, setAvisoPreset] = useState('No hay clases hoy');
  const [avisoTextoCustom, setAvisoTextoCustom] = useState('');
  const [avisoRazon, setAvisoRazon] = useState('');
  const [avisoEnviarPush, setAvisoEnviarPush] = useState(true);
  const [avisoGuardando, setAvisoGuardando] = useState(false);

  useEffect(() => {
    loadClaseHoy();
    loadEventosHoy();
    
    // Recargar cada 5 minutos
    const interval = setInterval(() => {
      loadClaseHoy();
      loadEventosHoy();
    }, 5 * 60 * 1000);
    
    return () => clearInterval(interval);
  }, []);

  const loadClaseHoy = async () => {
    try {
      const response = await axios.get(`${API}/profesor/mi-clase-hoy`);
      
      if (response.data.success && response.data.clases) {
        setClasesHoy(response.data.clases);
        // Precargar mapa de estudiantes (id → {nombre, foto_perfil}) para mostrar
        // avatars directamente en cada tarjeta, sin necesidad de abrir modal.
        const idsSet = new Set();
        response.data.clases.forEach(c => {
          (c.estudiantes_inscritos || []).forEach(id => idsSet.add(id));
        });
        if (idsSet.size > 0) {
          try {
            const usersRes = await axios.get(`${API}/users`);
            const map = {};
            (usersRes.data || []).forEach(u => {
              if (idsSet.has(u.id)) {
                map[u.id] = {
                  id: u.id,
                  nombre: u.nombre,
                  apellido: u.apellido,
                  foto_perfil: u.foto_perfil,
                };
              }
            });
            setEstudiantesMap(map);
          } catch (err) {
            console.error('Error precargando avatars estudiantes:', err);
          }
        }
      } else {
        setClasesHoy([]);
      }
    } catch (error) {
      console.error('Error cargando clases del día:', error);
    } finally {
      setLoading(false);
    }
  };

  const abrirAvisoModal = (clase) => {
    setAvisoClase(clase);
    const avisoActual = clase.aviso_hoy;
    if (avisoActual?.activo) {
      const presets = ['No hay clases hoy', 'Clase cancelada', 'Clase pospuesta'];
      if (presets.includes(avisoActual.texto)) {
        setAvisoPreset(avisoActual.texto);
        setAvisoTextoCustom('');
      } else {
        setAvisoPreset('otro');
        setAvisoTextoCustom(avisoActual.texto || '');
      }
      setAvisoRazon(avisoActual.razon || '');
    } else {
      setAvisoPreset('No hay clases hoy');
      setAvisoTextoCustom('');
      setAvisoRazon('');
    }
    setAvisoEnviarPush(true);
    setShowAvisoModal(true);
  };

  const cerrarAvisoModal = () => {
    setShowAvisoModal(false);
    setAvisoClase(null);
  };

  const guardarAviso = async (activar) => {
    if (!avisoClase) return;
    setAvisoGuardando(true);
    try {
      const texto = avisoPreset === 'otro' ? avisoTextoCustom.trim() : avisoPreset;
      const body = activar
        ? { activo: true, texto: texto || 'No hay clases hoy', razon: avisoRazon.trim(), enviar_push: avisoEnviarPush }
        : { activo: false };
      const resp = await axios.put(
        `${API}/admin/clases/${avisoClase.id}/aviso-hoy`,
        body,
        { headers: { Authorization: `Bearer ${localStorage.getItem('token')}` } }
      );
      if (activar) {
        const pushInfo = resp.data?.push_enviado_a ? ` (push enviado a ${resp.data.push_enviado_a})` : '';
        toast.success(`Aviso activado${pushInfo}`);
      } else {
        toast.success('Aviso removido');
      }
      cerrarAvisoModal();
      loadClaseHoy();
    } catch (err) {
      console.error('Error guardando aviso:', err);
      toast.error(err?.response?.data?.detail || 'Error al guardar el aviso');
    } finally {
      setAvisoGuardando(false);
    }
  };

  const loadEventosHoy = async () => {
    try {
      const response = await axios.get(`${API}/eventos/hoy`);
      
      if (response.data.success && response.data.eventos) {
        console.log('📅 Eventos del día cargados:', response.data.eventos);
        response.data.eventos.forEach(e => {
          console.log(`Evento: ${e.titulo} - imagen_url:`, e.imagen_url || 'NO TIENE');
        });
        setEventosHoy(response.data.eventos);
      } else {
        setEventosHoy([]);
      }
    } catch (error) {
      console.error('Error cargando eventos del día:', error);
      setEventosHoy([]);
    }
  };

  const abrirModalEstudiantes = async (clase) => {
    setSelectedClase(clase);
    setShowEstudiantesModal(true);
    setLoadingEstudiantes(true);
    
    try {
      const estudiantesIds = clase?.estudiantes_inscritos || [];
      
      console.log('IDs de estudiantes inscritos:', estudiantesIds);
      
      if (estudiantesIds.length === 0) {
        setEstudiantesInscritos([]);
        setLoadingEstudiantes(false);
        return;
      }
      
      // Obtener TODOS los usuarios de una sola vez (más eficiente)
      const response = await axios.get(`${API}/users`, {
        headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }
      });
      
      const todosLosUsuarios = response.data;
      console.log(`✅ Total usuarios obtenidos: ${todosLosUsuarios.length}`);
      
      // Filtrar solo los estudiantes inscritos en esta clase
      const estudiantesInscritos = todosLosUsuarios.filter(usuario => 
        estudiantesIds.includes(usuario.id)
      );
      
      console.log(`✅ Estudiantes de esta clase: ${estudiantesInscritos.length} de ${estudiantesIds.length}`);
      
      setEstudiantesInscritos(estudiantesInscritos);
    } catch (error) {
      console.error('Error cargando estudiantes:', error);
      toast.error('Error al cargar la lista de estudiantes');
    } finally {
      setLoadingEstudiantes(false);
    }
  };
  
  const cerrarModalEstudiantes = () => {
    setShowEstudiantesModal(false);
    setEstudiantesInscritos([]);
    setSelectedClase(null);
  };

  // Función auxiliar para extraer meeting ID y password del URL de Zoom
  const extraerDatosZoom = (zoomUrl) => {
    try {
      // Formato típico: https://us02web.zoom.us/j/86943396227?pwd=xxxxx
      const urlObj = new URL(zoomUrl);
      const pathParts = urlObj.pathname.split('/');
      const meetingId = pathParts[pathParts.length - 1];
      const password = urlObj.searchParams.get('pwd') || '';
      return { meetingId, password };
    } catch (e) {
      console.error('Error extrayendo datos de Zoom URL:', e);
      return { meetingId: null, password: null };
    }
  };

  // Función para mostrar modal de selección de método (solo para roles elegibles)
  const iniciarClase = async (clase) => {
    const esGrabada = (clase.modalidad || 'vivo') === 'grabada';
    const esParticipante = clase.tipo_participacion === 'estudiante';
    const zoomUrl = esParticipante ? clase.zoom_join_url : clase.zoom_start_url;

    if (!esGrabada && (!clase || !zoomUrl)) {
      toast.error('Esta clase no tiene una reunión de Zoom configurada');
      return;
    }

    try {
      setIniciandoClase(prev => ({ ...prev, [clase.id]: true }));

      // Registrar asistencia (idéntico para vivo y grabada)
      await axios.post(`${API}/profesor/registrar-asistencia-clase`, {
        clase_id: clase.id,
        clase_nombre: clase.nombre
      });

      toast.success('✅ Asistencia registrada');

      // 📼 Clase GRABADA: navegar a la sección de videos de la clase
      if (esGrabada) {
        navigate(`/app/clases/${clase.id}/videos`);
        return;
      }

      // Si la clase tiene SDK Embebido habilitado (feature flag por clase),
      // ir a la página de Clase en Vivo con Client View embebido.
      // EXCEPCIÓN: el profesor que dicta y el admin supervisor van a una pantalla
      // intermedia con instrucciones + credenciales + botón grande a Zoom.
      if (clase.usa_sdk_embebido) {
        const esProfesorDeClase = clase.tipo_participacion === 'profesor';
        const esAdminSupervisor = clase.tipo_participacion === 'admin';
        if (esProfesorDeClase || esAdminSupervisor) {
          navigate(`/app/clase-profesor-zoom?clase_id=${clase.id}`);
          return;
        }
        navigate(`/app/clase-en-vivo?clase_id=${clase.id}`);
        return;
      }

      // Usar SDK Client View embebido
      const { meetingId, password } = extraerDatosZoom(zoomUrl);
      
      if (meetingId) {
        const params = new URLSearchParams({
          meeting: meetingId,
          pwd: password || '',
          clase_id: clase.id || '',
          titulo: clase.nombre || 'Clase en Vivo',
          zoom_url: zoomUrl || ''  // Pasar URL original para preservar subdominio (us02web, us04web, etc.)
        });
        navigate(`/app/clase-zoom-beta?${params.toString()}`);
      } else {
        // Fallback: abrir Zoom directamente si no se puede extraer el meeting ID
        toast.info('Abriendo Zoom...');
        window.open(zoomUrl, '_blank');
      }

    } catch (error) {
      console.error('Error iniciando clase:', error);
      toast.error('Error al registrar asistencia');
    } finally {
      setIniciandoClase(prev => ({ ...prev, [clase.id]: false }));
    }
  };

  // Entrar con método seleccionado (asistencia ya fue marcada)
  const entrarConMetodo = async (metodo) => {
    if (!claseParaEntrar) return;
    
    setShowMetodoModal(false);
    const clase = claseParaEntrar;
    const zoomUrl = clase.zoom_join_url || clase.zoom_start_url;
    
    // Usar SDK Client View
    const { meetingId, password } = extraerDatosZoom(zoomUrl);
    if (meetingId) {
      const params = new URLSearchParams({
        meeting: meetingId,
        pwd: password || '',
        clase_id: clase.id || '',
        titulo: clase.nombre || 'Clase en Vivo',
        zoom_url: zoomUrl || ''  // Preservar subdominio original
      });
      navigate(`/app/clase-zoom-beta?${params.toString()}`);
    } else {
      window.open(zoomUrl, '_blank');
    }
    
    setClaseParaEntrar(null);
  };

  /* ============================================================
   * SISTEMA ANTIGUO COMPLETO (RESPALDO) - NO BORRAR
   * ============================================================
   * Para reactivar: cambiar USAR_SDK_EMBEBIDO = false arriba
   * 
   * const iniciarClaseAntiguo = async (clase) => {
   *   const esEstudiante = clase.tipo_participacion === 'estudiante';
   *   const zoomUrl = esEstudiante ? clase.zoom_join_url : clase.zoom_start_url;
   *   
   *   if (!clase || !zoomUrl) {
   *     toast.error('Esta clase no tiene una reunión de Zoom configurada');
   *     return;
   *   }
   *
   *   try {
   *     setIniciandoClase(prev => ({ ...prev, [clase.id]: true }));
   *
   *     await axios.post(`${API}/profesor/registrar-asistencia-clase`, {
   *       clase_id: clase.id,
   *       clase_nombre: clase.nombre
   *     });
   *
   *     toast.success(esEstudiante ? 'Uniéndose a la clase...' : 'Asistencia registrada. Abriendo Zoom...');
   *     window.open(zoomUrl, '_blank');
   *
   *   } catch (error) {
   *     console.error('Error iniciando clase:', error);
   *     toast.error('Error al unirse a la clase');
   *   } finally {
   *     setIniciandoClase(prev => ({ ...prev, [clase.id]: false }));
   *   }
   * };
   * ============================================================
   */


  // Enviar link de emergencia al Chat General
  const enviarLinkEmergencia = async () => {
    if (!claseEmergencia) return;
    setEnviandoEmergencia(true);
    try {
      const zoomUrl = claseEmergencia.zoom_join_url || claseEmergencia.zoom_start_url;
      if (!zoomUrl) {
        toast.error('Esta clase no tiene enlace de Zoom configurado');
        return;
      }
      const mensaje = `🚨 LINK DE EMERGENCIA - ${claseEmergencia.nombre}\n\nEl profesor ha compartido el link de acceso:\n${zoomUrl}\n\nHaz clic en el enlace para unirte a la clase.`;
      await axios.post(`${API}/chat/messages`, {
        content: mensaje,
        target_type: 'channel',
        target_id: 'general'
      });
      toast.success(`Link de "${claseEmergencia.nombre}" enviado al Chat General`);
    } catch (error) {
      console.error('Error enviando link de emergencia:', error);
      toast.error('Error al enviar el link al chat');
    } finally {
      setEnviandoEmergencia(false);
      setShowEmergenciaConfirm(false);
      setClaseEmergencia(null);
    }
  };

  if (loading) {
    return (
      <Card className="p-6 text-center">
        <p>Cargando clases y eventos del día...</p>
      </Card>
    );
  }

  const totalItems = (clasesHoy?.length || 0) + (eventosHoy?.length || 0);

  if (totalItems === 0) {
    return null;
  }

  // Determinar si hay clases HOY
  const clasesDeHoy = clasesHoy.filter(c => c.es_dia_de_clase !== false);
  const hayClaseHoy = clasesDeHoy.length > 0;
  const todasDisponibles = clasesHoy.length > 0 && clasesHoy.every(c => c.tipo_participacion === 'disponible');

  return (
    <div>
      {/* Título de la sección */}
      <h2 className="text-2xl font-bold text-gray-800 mb-4 flex items-center gap-2">
        <span>📅</span>
        {todasDisponibles ? 'Clases del Semestre' : hayClaseHoy ? 'Clases y Eventos de Hoy' : 'Mis Clases del Semestre'}
        <Badge className="bg-blue-600 text-white ml-2">{totalItems}</Badge>
      </h2>
      
      {/* Banner informativo si no está inscrito */}
      {todasDisponibles && (
        <div className="mb-4 p-3 bg-amber-50 border border-amber-200 rounded-lg flex items-center gap-3" data-testid="inscripcion-pendiente-banner">
          <i className="fas fa-info-circle text-amber-500 text-lg"></i>
          <p className="text-sm text-amber-800">
            Aún no estás inscrito en clases. Estas son las clases disponibles en el semestre. Completa tu proceso de registro para inscribirte.
          </p>
        </div>
      )}
      
      {/* Grid de clases y eventos - máximo 4 por fila en pantallas grandes */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4 mb-6">
        {/* Renderizar Clases */}
        {clasesHoy.map((clase) => (
          <ClaseCard 
            key={`clase-${clase.id}`} 
            clase={clase}
            iniciando={iniciandoClase[clase.id]}
            onIniciar={() => iniciarClase(clase)}
            onVerEstudiantes={() => abrirModalEstudiantes(clase)}
            userRol={user?.rol}
            onEmergencia={(c) => { setClaseEmergencia(c); setShowEmergenciaConfirm(true); }}
            onAbrirAviso={() => abrirAvisoModal(clase)}
            estudiantesMap={estudiantesMap}
          />
        ))}
        
        {/* Renderizar Eventos */}
        {eventosHoy.map((evento) => (
          <EventoCard 
            key={`evento-${evento.id}`} 
            evento={evento}
          />
        ))}
      </div>
      
      {/* Modal de Estudiantes Inscritos */}
      {showEstudiantesModal && (
        <EstudiantesModal 
          isOpen={showEstudiantesModal}
          onClose={cerrarModalEstudiantes}
          clase={selectedClase}
          estudiantes={estudiantesInscritos}
          loading={loadingEstudiantes}
        />
      )}

      {/* Modal de selección de método de entrada */}
      {showMetodoModal && claseParaEntrar && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl shadow-2xl max-w-md w-full overflow-hidden animate-in zoom-in-95 duration-200">
            {/* Header */}
            <div className="bg-gradient-to-r from-blue-600 to-indigo-600 p-4 text-white">
              <h3 className="text-lg font-bold">¿Cómo deseas ingresar a la clase?</h3>
              <p className="text-sm opacity-90 mt-1">{claseParaEntrar.nombre}</p>
            </div>
            
            {/* Opciones */}
            <div className="p-4 space-y-3">
              {/* Opción Zoom Directo - RECOMENDADA */}
              <button
                onClick={() => entrarConMetodo('directo')}
                className="w-full p-4 border-2 border-green-500 rounded-xl hover:bg-green-50 transition-all text-left relative"
              >
                <div className="absolute -top-2 -right-2 bg-green-500 text-white text-xs px-2 py-1 rounded-full font-bold">
                  ⭐ RECOMENDADO
                </div>
                <div className="flex items-start gap-3">
                  <div className="text-3xl">📱</div>
                  <div>
                    <h4 className="font-bold text-green-700">Abrir Zoom Directamente</h4>
                    <p className="text-sm text-gray-600 mt-1">
                      Se abrirá la aplicación de Zoom. 
                      <span className="text-green-600 font-medium"> Mejor para cámara y audio.</span>
                    </p>
                  </div>
                </div>
              </button>

              {/* Opción SDK Embebido */}
              <button
                onClick={() => entrarConMetodo('sdk')}
                className="w-full p-4 border-2 border-gray-300 rounded-xl hover:bg-gray-50 transition-all text-left"
              >
                <div className="flex items-start gap-3">
                  <div className="text-3xl">💻</div>
                  <div>
                    <h4 className="font-bold text-gray-700">Ver en esta página</h4>
                    <p className="text-sm text-gray-600 mt-1">
                      La clase se mostrará aquí con herramientas de Biblia y Notas.
                      <span className="text-amber-600 font-medium"> Solo recomendado en computadora.</span>
                    </p>
                  </div>
                </div>
              </button>

              {/* Opción BETA: SDK Embebido Real (solo admin/profesor) */}
              {(user?.rol === 'admin' || user?.rol === 'profesor') && (
                <button
                  onClick={() => entrarConMetodo('sdk-beta')}
                  className="w-full p-4 border-2 border-indigo-400 rounded-xl hover:bg-indigo-50 transition-all text-left relative"
                >
                  <div className="absolute -top-2 -right-2 bg-yellow-500 text-white text-xs px-2 py-1 rounded-full font-bold">
                    BETA
                  </div>
                  <div className="flex items-start gap-3">
                    <div className="text-3xl">🧪</div>
                    <div>
                      <h4 className="font-bold text-indigo-700">Nueva Experiencia Embebida</h4>
                      <p className="text-sm text-gray-600 mt-1">
                        Video de Zoom integrado en la página con Biblia y Notas al lado.
                        <span className="text-indigo-600 font-medium"> En pruebas - solo profesores.</span>
                      </p>
                    </div>
                  </div>
                </button>
              )}
            </div>

            {/* Footer */}
            <div className="bg-gray-50 px-4 py-3 flex justify-end">
              <button
                onClick={() => {
                  setShowMetodoModal(false);
                  setClaseParaEntrar(null);
                }}
                className="px-4 py-2 text-gray-600 hover:text-gray-800 font-medium"
              >
                Cancelar
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Modal de confirmación de Link de Emergencia */}
      {showEmergenciaConfirm && claseEmergencia && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl shadow-2xl max-w-sm w-full overflow-hidden animate-in zoom-in-95 duration-200">
            <div className="bg-amber-500 p-4 text-white">
              <h3 className="text-lg font-bold flex items-center gap-2">
                <i className="fas fa-exclamation-triangle"></i>
                Link de Emergencia
              </h3>
            </div>
            <div className="p-4">
              <p className="text-gray-700 text-sm">
                Se enviará el link de Zoom de <strong>{claseEmergencia.nombre}</strong> al <strong>Chat General</strong> para que todos los estudiantes puedan acceder.
              </p>
              <p className="text-xs text-gray-500 mt-2">Usa esto solo si los estudiantes tienen problemas para entrar.</p>
            </div>
            <div className="bg-gray-50 px-4 py-3 flex justify-end gap-2">
              <button
                onClick={() => { setShowEmergenciaConfirm(false); setClaseEmergencia(null); }}
                className="px-4 py-2 text-gray-600 hover:text-gray-800 font-medium text-sm"
                disabled={enviandoEmergencia}
              >
                Cancelar
              </button>
              <button
                onClick={enviarLinkEmergencia}
                disabled={enviandoEmergencia}
                className="px-4 py-2 bg-amber-500 hover:bg-amber-600 text-white rounded-lg font-medium text-sm transition-colors"
                data-testid="confirmar-emergencia-btn"
              >
                {enviandoEmergencia ? (
                  <><i className="fas fa-spinner fa-spin mr-1"></i>Enviando...</>
                ) : (
                  <><i className="fas fa-paper-plane mr-1"></i>Enviar al Chat</>
                )}
              </button>
            </div>
          </div>
        </div>
      )}
      {/* Modal Admin: "No hay clase hoy" */}
      {showAvisoModal && avisoClase && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4" data-testid="aviso-modal">
          <div className="bg-white rounded-2xl shadow-2xl max-w-md w-full overflow-hidden animate-in zoom-in-95 duration-200">
            <div className="bg-gradient-to-r from-red-600 to-rose-500 p-4 text-white">
              <h3 className="text-lg font-bold flex items-center gap-2">
                <i className="fas fa-ban"></i>
                {avisoClase.aviso_hoy?.activo ? 'Editar aviso' : 'Marcar sin clase hoy'}
              </h3>
              <p className="text-xs text-white/85 mt-1">{avisoClase.nombre}</p>
            </div>
            <div className="p-4 space-y-3">
              <div>
                <label className="text-xs font-semibold text-gray-700 mb-1 block">Texto del banner</label>
                <div className="grid grid-cols-2 gap-2">
                  {['No hay clases hoy', 'Clase cancelada', 'Clase pospuesta', 'otro'].map((p) => (
                    <button
                      key={p}
                      type="button"
                      onClick={() => setAvisoPreset(p)}
                      className={`text-xs font-semibold py-2 px-3 rounded-lg border-2 transition-all ${
                        avisoPreset === p
                          ? 'bg-red-500 text-white border-red-600 shadow-md'
                          : 'bg-white text-gray-700 border-gray-200 hover:border-red-300'
                      }`}
                      data-testid={`aviso-preset-${p}`}
                    >
                      {p === 'otro' ? '✏️ Otro' : p}
                    </button>
                  ))}
                </div>
                {avisoPreset === 'otro' && (
                  <input
                    type="text"
                    value={avisoTextoCustom}
                    onChange={(e) => setAvisoTextoCustom(e.target.value)}
                    placeholder="Escribí el texto del banner..."
                    maxLength={120}
                    className="mt-2 w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:border-red-500 focus:outline-none"
                    data-testid="aviso-texto-custom"
                  />
                )}
              </div>

              <div>
                <label className="text-xs font-semibold text-gray-700 mb-1 block">
                  Razón (opcional) <span className="font-normal text-gray-400">— se muestra al pie del card</span>
                </label>
                <input
                  type="text"
                  value={avisoRazon}
                  onChange={(e) => setAvisoRazon(e.target.value)}
                  placeholder="Ej: Profesor enfermo, Feriado, Reunión pastoral..."
                  maxLength={300}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:border-red-500 focus:outline-none"
                  data-testid="aviso-razon"
                />
              </div>

              <label className="flex items-center gap-2 text-sm text-gray-700 cursor-pointer select-none">
                <input
                  type="checkbox"
                  checked={avisoEnviarPush}
                  onChange={(e) => setAvisoEnviarPush(e.target.checked)}
                  className="w-4 h-4 accent-red-500"
                  data-testid="aviso-push-toggle"
                />
                <span>🔔 Enviar notificación push a los inscriptos</span>
              </label>

              <p className="text-[11px] text-gray-500 italic">
                ⏰ El aviso se elimina automáticamente al cambiar de día.
              </p>
            </div>
            <div className="bg-gray-50 px-4 py-3 flex justify-between gap-2">
              {avisoClase.aviso_hoy?.activo ? (
                <button
                  onClick={() => guardarAviso(false)}
                  disabled={avisoGuardando}
                  className="px-3 py-2 text-red-600 hover:bg-red-50 font-medium text-sm rounded-lg"
                  data-testid="aviso-remove-btn"
                >
                  <i className="fas fa-trash mr-1"></i> Quitar aviso
                </button>
              ) : (
                <span />
              )}
              <div className="flex gap-2">
                <button
                  onClick={cerrarAvisoModal}
                  disabled={avisoGuardando}
                  className="px-4 py-2 text-gray-600 hover:text-gray-800 font-medium text-sm"
                >
                  Cancelar
                </button>
                <button
                  onClick={() => guardarAviso(true)}
                  disabled={avisoGuardando || (avisoPreset === 'otro' && !avisoTextoCustom.trim())}
                  className="px-4 py-2 bg-red-500 hover:bg-red-600 disabled:bg-gray-300 text-white rounded-lg font-medium text-sm transition-colors"
                  data-testid="aviso-save-btn"
                >
                  {avisoGuardando ? (
                    <><i className="fas fa-spinner fa-spin mr-1"></i>Guardando...</>
                  ) : (
                    <><i className="fas fa-check mr-1"></i>{avisoClase.aviso_hoy?.activo ? 'Actualizar' : 'Activar aviso'}</>
                  )}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

// Componente para cada tarjeta de clase - COMPACTA Y CUADRADA
const ClaseCard = ({ clase, iniciando, onIniciar, onVerEstudiantes, userRol, onEmergencia, onAbrirAviso, estudiantesMap = {} }) => {
  const navigate = useNavigate();
  const esEstudiante = clase.tipo_participacion === 'estudiante';
  const esAdmin = clase.tipo_participacion === 'admin';
  const esDisponible = clase.tipo_participacion === 'disponible';
  const esDiaDeClase = clase.es_dia_de_clase !== false; // true si es hoy o no está definido
  const diaClaseTexto = clase.dia_clase_texto || 'Hoy';
  const puedeEmergencia = ['admin', 'profesor'].includes(userRol?.toLowerCase());
  const esAdminUser = (userRol || '').toLowerCase() === 'admin';
  // Profesor titular = el profesor que dicta esta clase (tipo_participacion === 'profesor')
  const esProfesorTitular = (userRol || '').toLowerCase() === 'profesor' && clase.tipo_participacion === 'profesor';
  // Tanto admin como profesor titular pueden marcar "sin clase hoy"
  const puedeMarcarAviso = esAdminUser || esProfesorTitular;
  const esAutocapacitacion = clase.categoria === 'autocapacitacion' || clase.categoria === 'autocapacitacion_profesores';
  // Gating por fechas del curso: si todavía no inició o ya terminó, deshabilitar "Entrar a Clase"
  const cursoNoIniciado = clase.curso_iniciado === false;
  const cursoFinalizado = clase.curso_finalizado === true;

  // Aviso "No hay clase hoy"
  const avisoHoy = clase.aviso_hoy;
  const avisoActivo = !!(avisoHoy && avisoHoy.activo);
  
  // Colores según tipo de participación
  const borderColor = esDisponible ? 'border-gray-300' : esAdmin ? 'border-purple-400' : esEstudiante ? 'border-green-400' : 'border-blue-400';
  const gradientColor = esDisponible ? 'from-gray-400 to-gray-500' : esAdmin ? 'from-purple-500 to-indigo-500' : esEstudiante ? 'from-green-500 to-emerald-500' : 'from-blue-500 to-purple-500';
  const badgeText = esDisponible ? 'Disponible' : esAdmin ? '👑 Supervisor' : esEstudiante ? '🎓 Capacitación' : '📚 Dicto';
  const badgeColor = esDisponible ? 'bg-gray-500' : esAdmin ? 'bg-purple-600' : esEstudiante ? 'bg-green-600' : 'bg-blue-600';

  return (
    <Card
      className={`relative border-2 ${avisoActivo ? 'border-red-500' : borderColor} shadow-lg overflow-hidden hover:shadow-xl transition-all duration-300 hover:scale-[1.02]`}
      data-testid={`clase-card-${clase.id}`}
    >
      {/* Cinta diagonal "No hay clase hoy" */}
      {avisoActivo && (
        <>
          {/* Overlay semi-transparente que apaga el card */}
          <div
            className="absolute inset-0 z-20 bg-gray-900/50 backdrop-grayscale pointer-events-none"
            data-testid={`aviso-overlay-${clase.id}`}
          />
          {/* Cinta diagonal roja */}
          <div
            className="absolute z-30 pointer-events-none"
            style={{
              top: '28px',
              right: '-52px',
              width: '220px',
              transform: 'rotate(40deg)',
              transformOrigin: 'center',
            }}
          >
            <div className="bg-gradient-to-r from-red-600 via-red-500 to-red-600 text-white py-1.5 px-4 text-center shadow-2xl border-y-2 border-red-800">
              <span className="text-xs font-black uppercase tracking-wider drop-shadow-md">
                {avisoHoy.texto || 'No hay clase hoy'}
              </span>
            </div>
          </div>
          {/* Razón al pie (si hay) - clickeable tap para modal si admin */}
          {avisoHoy.razon && (
            <div className="absolute bottom-0 left-0 right-0 z-30 bg-red-600/95 text-white text-[11px] text-center py-1 px-2 font-semibold shadow-lg">
              <i className="fas fa-info-circle mr-1"></i>
              {avisoHoy.razon}
            </div>
          )}
        </>
      )}

      {/* Botón admin/profesor titular: "Marcar sin clase" / "Quitar aviso" */}
      {puedeMarcarAviso && onAbrirAviso && (
        <button
          type="button"
          onClick={(e) => { e.stopPropagation(); onAbrirAviso(); }}
          className={`absolute top-11 left-2 z-40 px-2 py-1 rounded-full text-[10px] font-bold shadow-lg transition-all hover:scale-110 ${
            avisoActivo
              ? 'bg-white text-red-700 border border-red-400'
              : 'bg-black/60 text-white hover:bg-red-600 backdrop-blur-sm'
          }`}
          title={avisoActivo ? 'Quitar aviso de "No hay clase"' : 'Marcar como "No hay clase hoy"'}
          data-testid={`aviso-btn-${clase.id}`}
        >
          {avisoActivo ? (
            <><i className="fas fa-times-circle mr-1"></i>Quitar aviso</>
          ) : (
            <><i className="fas fa-ban mr-1"></i>Sin clase hoy</>
          )}
        </button>
      )}
      {/* Banner/Imagen de la clase - Compacto */}
      {(clase.banner_url || clase.imagen_url) ? (
        <div className="relative h-32 overflow-hidden">
          <img 
            src={clase.banner_url || clase.imagen_url} 
            alt={clase.nombre}
            className="w-full h-full object-cover"
            onError={(e) => {
              e.target.style.display = 'none';
            }}
          />
          <div className="absolute inset-0 bg-gradient-to-t from-black/70 to-transparent"></div>
          
          {/* Badge de tipo de participación */}
          <div className="absolute top-2 right-2">
            <Badge className={`${badgeColor} text-white text-xs`}>
              {badgeText}
            </Badge>
          </div>
          
          {/* Badge del día (siempre visible para consistencia visual) */}
          {diaClaseTexto && (
            <div className="absolute top-2 left-2">
              <Badge className="bg-orange-500 text-white text-xs font-bold">
                📅 {diaClaseTexto}
              </Badge>
            </div>
          )}
          
          {/* Título sobre la imagen */}
          <div className="absolute bottom-0 left-0 right-0 p-3">
            <h3 className="text-base font-bold text-white drop-shadow-lg line-clamp-2">
              {clase.nombre}
            </h3>
          </div>
        </div>
      ) : (
        <div className={`bg-gradient-to-r ${gradientColor} p-4 relative`}>
          {/* Badge de tipo de participación */}
          <div className="absolute top-2 right-2">
            <Badge className="bg-white/20 text-white text-xs">
              {badgeText}
            </Badge>
          </div>
          {/* Badge del día (siempre visible para consistencia visual) */}
          {diaClaseTexto && (
            <div className="absolute top-2 left-2">
              <Badge className="bg-orange-500 text-white text-xs font-bold">
                📅 {diaClaseTexto}
              </Badge>
            </div>
          )}
          <h3 className="text-base font-bold text-white line-clamp-2 mt-4">
            {clase.nombre}
          </h3>
        </div>
      )}

      {/* Información compacta */}
      <CardContent className="p-3 space-y-2">
        {/* Horario */}
        {clase.hora_inicio && clase.hora_fin && clase.hora_inicio !== '00:00' ? (
          <div className="flex items-center gap-2 text-sm">
            <div className="bg-blue-100 rounded p-1.5">
              <i className="fas fa-clock text-blue-600 text-xs"></i>
            </div>
            <span className="font-medium text-gray-700 text-xs">
              {clase.hora_inicio} - {clase.hora_fin}
            </span>
          </div>
        ) : (
          <div className="flex items-center gap-2 text-sm">
            <div className="bg-yellow-100 rounded p-1.5">
              <i className="fas fa-exclamation-triangle text-yellow-600 text-xs"></i>
            </div>
            <span className="font-medium text-gray-500 text-xs italic">
              Sin horario configurado
            </span>
          </div>
        )}

        {/* Profesor */}
        {clase.profesor_nombre && (
          <div className="flex items-center gap-2 text-sm" data-testid={`clase-profesor-${clase.id}`}>
            {clase.profesor_foto ? (
              <img
                src={clase.profesor_foto}
                alt={clase.profesor_nombre}
                className="w-6 h-6 rounded-full object-cover border border-indigo-200 shadow-sm flex-shrink-0"
              />
            ) : (
              <div className="bg-indigo-100 rounded-full w-6 h-6 flex items-center justify-center flex-shrink-0">
                <i className="fas fa-chalkboard-teacher text-indigo-600 text-xs"></i>
              </div>
            )}
            <span className="font-medium text-gray-700 text-xs truncate">
              {clase.profesor_nombre}
            </span>
          </div>
        )}

        {/* Fechas del curso */}
        {(clase.fecha_inicio_curso || clase.fecha_fin_curso) && (
          <div className="flex items-center gap-2 text-sm">
            <div className="bg-purple-100 rounded p-1.5">
              <i className="fas fa-calendar-alt text-purple-600 text-xs"></i>
            </div>
            <span className="font-medium text-gray-600 text-xs">
              {clase.fecha_inicio_curso && clase.fecha_fin_curso 
                ? `${clase.fecha_inicio_curso} — ${clase.fecha_fin_curso}`
                : clase.fecha_inicio_curso 
                  ? `Inicio: ${clase.fecha_inicio_curso}`
                  : `Fin: ${clase.fecha_fin_curso}`
              }
            </span>
          </div>
        )}
        
        {/* Estudiantes - avatars directos. Se muestran a profesor/admin siempre;
            en clases DEVOCIONALES también se muestran a profesores que estén
            como "estudiante" (tipo_participacion=estudiante). Los estudiantes
            regulares no ven la lista. */}
        {(() => {
          const esDevocional = (clase.categoria || '').toLowerCase() === 'devocional';
          const puedeVerAvatars = !esEstudiante || (esDevocional && (userRol === 'admin' || userRol === 'profesor'));
          if (!puedeVerAvatars) return null;

          const ids = clase.estudiantes_inscritos || [];
          const presentesHoy = new Set(clase.estudiantes_presentes_hoy || []);
          const estudiantes = ids
            .map(id => estudiantesMap[id])
            .filter(Boolean);
          const visibles = estudiantes.slice(0, 5);
          const restantes = estudiantes.length - visibles.length;
          const total = clase.estudiantes_count ?? estudiantes.length ?? ids.length;
          const totalPresentes = presentesHoy.size;

          const iniciales = (est) => {
            const n = (est?.nombre || '').trim();
            const a = (est?.apellido || '').trim();
            return `${n[0] || ''}${a[0] || ''}`.toUpperCase() || '?';
          };

          const renderAvatar = (est) => {
            const presente = presentesHoy.has(est.id);
            const nombreCompleto = `${est.nombre || ''} ${est.apellido || ''}`.trim();
            return (
              <div key={est.id} className="relative">
                {est.foto_perfil ? (
                  <img
                    src={est.foto_perfil}
                    alt={est.nombre}
                    title={`${nombreCompleto}${presente ? ' · ✅ Ya ingresó' : ''}`}
                    className="w-7 h-7 rounded-full border-2 border-white object-cover shadow-sm"
                  />
                ) : (
                  <div
                    title={`${nombreCompleto}${presente ? ' · ✅ Ya ingresó' : ''}`}
                    className="w-7 h-7 rounded-full border-2 border-white bg-gradient-to-br from-blue-400 to-indigo-500 text-white text-[10px] font-semibold flex items-center justify-center shadow-sm"
                  >
                    {iniciales(est)}
                  </div>
                )}
                {presente && (
                  <span
                    className="absolute -bottom-0.5 -right-0.5 w-2.5 h-2.5 rounded-full bg-green-500 border-2 border-white shadow-sm"
                    title="Ya ingresó a clase"
                    data-testid={`avatar-presente-${est.id}`}
                  />
                )}
              </div>
            );
          };

          return (
            <div
              className="flex items-center gap-2 text-sm cursor-pointer hover:bg-blue-50 rounded p-1 -ml-1 transition-colors group"
              onClick={onVerEstudiantes}
              title={`Ver los ${total} estudiantes inscritos${totalPresentes > 0 ? ` · ${totalPresentes} ya ingresaron` : ''}`}
              data-testid={`clase-estudiantes-avatars-${clase.id}`}
            >
              {estudiantes.length > 0 ? (
                <div className="flex -space-x-2">
                  {visibles.map(est => renderAvatar(est))}
                  {restantes > 0 && (
                    <div
                      className="w-7 h-7 rounded-full border-2 border-white bg-gray-200 text-gray-700 text-[10px] font-semibold flex items-center justify-center shadow-sm"
                      title={`+${restantes} más`}
                    >
                      +{restantes}
                    </div>
                  )}
                </div>
              ) : (
                <div className="bg-green-100 rounded p-1.5">
                  <i className="fas fa-users text-green-600 text-xs"></i>
                </div>
              )}
              <span className="text-xs text-gray-600 group-hover:text-blue-600">
                {total} {total === 1 ? 'estudiante' : 'estudiantes'}
                {totalPresentes > 0 && (
                  <span className="ml-1 text-green-600 font-semibold" data-testid={`presentes-count-${clase.id}`}>
                    · {totalPresentes} ✓
                  </span>
                )}
              </span>
            </div>
          );
        })()}
        
        {/* Botón de Zoom - Compacto */}
        {/* Si es clase disponible (no inscrito), mostrar mensaje de inscripción */}
        {esDisponible ? (
          <div className="bg-amber-50 border border-amber-300 rounded p-2 mt-2">
            <p className="text-amber-700 text-xs text-center font-medium" data-testid="clase-disponible-msg">
              <i className="fas fa-info-circle mr-1"></i>
              Completa tu inscripción para acceder
            </p>
          </div>
        ) : esAutocapacitacion ? (
          <Button
            onClick={() => navigate(`/app/pruebas-examenes?clase_id=${clase.id}`)}
            className="w-full mt-2 bg-gradient-to-r from-orange-200 via-pink-200 to-rose-300 hover:from-orange-300 hover:via-pink-300 hover:to-rose-400 text-rose-900 font-semibold py-2 text-sm shadow-md hover:shadow-lg transition-all"
            data-testid={`entrar-autocapacitacion-${clase.id}`}
          >
            <i className="fas fa-graduation-cap mr-1 text-xs"></i>
            Entrar a Autocapacitación
          </Button>
        ) : cursoNoIniciado && clase.modalidad !== 'grabada' ? (
          <div className="bg-blue-50 border border-blue-300 rounded p-2 mt-2" data-testid={`curso-no-iniciado-${clase.id}`}>
            <p className="text-blue-700 text-xs text-center font-medium">
              <i className="fas fa-calendar-day mr-1"></i>
              Inicia el {clase.fecha_inicio_curso || 'próximamente'}
            </p>
          </div>
        ) : cursoFinalizado && clase.modalidad !== 'grabada' ? (
          <div className="bg-gray-100 border border-gray-300 rounded p-2 mt-2" data-testid={`curso-finalizado-${clase.id}`}>
            <p className="text-gray-600 text-xs text-center font-medium">
              <i className="fas fa-flag-checkered mr-1"></i>
              Curso finalizado el {clase.fecha_fin_curso || ''}
            </p>
          </div>
        ) : (clase.modalidad === 'grabada' || (esEstudiante ? clase.zoom_join_url : clase.zoom_start_url)) ? (
          (clase.modalidad === 'grabada' || esDiaDeClase) ? (
            <div className="flex items-center gap-1 mt-2">
              <Button
                onClick={onIniciar}
                disabled={iniciando}
                className={`flex-1 bg-gradient-to-r ${
                  clase.modalidad === 'grabada'
                    ? 'from-purple-500 to-fuchsia-600 hover:from-purple-600 hover:to-fuchsia-700'
                    : esEstudiante
                      ? 'from-green-500 to-emerald-600 hover:from-green-600 hover:to-emerald-700'
                      : 'from-blue-500 to-indigo-600 hover:from-blue-600 hover:to-indigo-700'
                } text-white font-semibold py-2 text-sm shadow-md hover:shadow-lg transition-all`}
                data-testid={`btn-entrar-clase-${clase.id}`}
              >
                {iniciando ? (
                  <>
                    <i className="fas fa-spinner fa-spin mr-1 text-xs"></i>
                    {clase.modalidad === 'grabada' ? 'Registrando...' : 'Conectando...'}
                  </>
                ) : (
                  <>
                    <i className={`fas ${clase.modalidad === 'grabada' ? 'fa-play-circle' : 'fa-video'} mr-1 text-xs`}></i>
                    {clase.modalidad === 'grabada' ? 'Ver Clase Grabada' : 'Entrar a Clase'}
                  </>
                )}
              </Button>
              {puedeEmergencia && (clase.zoom_join_url || clase.zoom_start_url) && (
                <button
                  onClick={() => onEmergencia(clase)}
                  className="p-2 text-gray-400 hover:text-amber-500 hover:bg-amber-50 rounded-lg transition-colors"
                  title="Enviar link al Chat General"
                  data-testid="emergencia-link-btn"
                >
                  <i className="fas fa-share-alt text-sm"></i>
                </button>
              )}
            </div>
          ) : (
            <div className="bg-gray-100 border border-gray-300 rounded p-2 mt-2">
              <p className="text-gray-500 text-xs text-center">
                <i className="fas fa-clock mr-1"></i>
                Clase disponible el {diaClaseTexto}
              </p>
            </div>
          )
        ) : (
          <div className="bg-yellow-50 border border-yellow-300 rounded p-2 mt-2">
            <p className="text-yellow-700 text-xs text-center">
              <i className="fas fa-exclamation-triangle mr-1"></i>
              Sin Zoom
            </p>
          </div>
        )}

        {/* QR de asistencia presencial embebido — visible directamente en la tarjeta.
            Escanear o tocar registra asistencia sin abrir Zoom. */}
        {!esAutocapacitacion && (() => {
          const qrUrl = `${window.location.origin}/marcar-asistencia-qr?clase_id=${clase.id}`;
          return (
            <div className="mt-2 flex items-center gap-2 p-2 bg-indigo-50 border border-indigo-200 rounded-lg">
              <a
                href={qrUrl}
                target="_blank"
                rel="noopener noreferrer"
                className="shrink-0 bg-white p-1 rounded border border-indigo-200 hover:border-indigo-500 transition-colors"
                title="Escanear o tocar para marcar asistencia presencial"
                data-testid={`qr-presencial-inline-${clase.id}`}
              >
                <QRCodeSVG value={qrUrl} size={64} level="M" includeMargin={false} />
              </a>
              <div className="flex-1 min-w-0">
                <p className="text-[11px] font-semibold text-indigo-800 leading-tight">
                  Asistencia presencial
                </p>
                <p className="text-[10px] text-indigo-600 leading-tight mt-0.5">
                  Escaneá o tocá el QR para marcar sin abrir Zoom
                </p>
              </div>
            </div>
          );
        })()}
      </CardContent>
    </Card>
  );
};


// Componente para cada tarjeta de evento
const EventoCard = ({ evento }) => {
  const navigate = useNavigate();
  const formatearHora = (fecha) => {
    try {
      // MISMA LÓGICA QUE LANDING PAGE - Convertir UTC a hora de Montevideo
      const utcDate = new Date(fecha);
      const montevideoDate = new Date(utcDate.getTime() - (3 * 60 * 60 * 1000));
      return montevideoDate.toLocaleTimeString('es-UY', {
        hour: '2-digit',
        minute: '2-digit',
        hour12: false
      });
    } catch (e) {
      return '';
    }
  };

  const abrirZoom = () => {
    if (evento.link_zoom) {
      try {
        const urlObj = new URL(evento.link_zoom);
        const pathParts = urlObj.pathname.split('/');
        const meetingId = pathParts[pathParts.length - 1];
        const password = urlObj.searchParams.get('pwd') || '';
        if (meetingId) {
          const params = new URLSearchParams({
            meeting: meetingId,
            pwd: password,
            titulo: evento.titulo || 'Evento en Vivo',
            zoom_url: evento.link_zoom || ''
          });
          navigate(`/app/clase-zoom-beta?${params.toString()}`);
          return;
        }
      } catch (e) {}
      window.open(evento.link_zoom, '_blank');
    } else {
      toast.error('Este evento no tiene enlace de Zoom configurado');
    }
  };

  return (
    <Card className="border-2 border-purple-400 shadow-lg overflow-hidden hover:shadow-xl transition-all duration-300 hover:scale-[1.02]">
      {/* Banner/Imagen del evento - Compacto */}
      {evento.imagen_url ? (
        <div className="relative h-32 overflow-hidden">
          <img 
            src={evento.imagen_url} 
            alt={evento.titulo}
            className="w-full h-full object-cover"
            onError={(e) => {
              e.target.style.display = 'none';
              e.target.parentElement.classList.add('hidden');
              e.target.parentElement.nextElementSibling.classList.remove('hidden');
            }}
          />
          <div className="absolute inset-0 bg-gradient-to-t from-black/70 to-transparent"></div>
          
          {/* Título sobre la imagen */}
          <div className="absolute bottom-0 left-0 right-0 p-3">
            <div className="flex items-center gap-2 mb-1">
              <Badge className="bg-purple-600 text-white text-xs">
                {evento.tipo === 'online' ? '🌐 Online' : '📍 Presencial'}
              </Badge>
            </div>
            <h3 className="text-base font-bold text-white drop-shadow-lg line-clamp-2">
              {evento.titulo}
            </h3>
          </div>
        </div>
      ) : (
        <div className="bg-gradient-to-r from-purple-500 to-pink-500 p-4 hidden">
          <div className="flex items-center gap-2 mb-1">
            <Badge className="bg-white text-purple-700 text-xs">
              {evento.tipo === 'online' ? '🌐 Online' : '📍 Presencial'}
            </Badge>
          </div>
          <h3 className="text-base font-bold text-white line-clamp-2">
            {evento.titulo}
          </h3>
        </div>
      )}

      {/* Fallback si no hay imagen */}
      {!evento.imagen_url && (
        <div className="bg-gradient-to-r from-purple-500 to-pink-500 p-4">
          <div className="flex items-center gap-2 mb-1">
            <Badge className="bg-white text-purple-700 text-xs">
              {evento.tipo === 'online' ? '🌐 Online' : '📍 Presencial'}
            </Badge>
          </div>
          <h3 className="text-base font-bold text-white line-clamp-2">
            {evento.titulo}
          </h3>
        </div>
      )}

      {/* Información compacta */}
      <CardContent className="p-3 space-y-2">
        {/* Descripción */}
        <p className="text-xs text-gray-600 line-clamp-2">
          {evento.descripcion}
        </p>

        {/* Horario */}
        {evento.fecha_inicio && evento.fecha_fin && (
          <div className="flex items-center gap-2 text-sm">
            <div className="bg-purple-100 rounded p-1.5">
              <i className="fas fa-clock text-purple-600 text-xs"></i>
            </div>
            <span className="font-semibold text-gray-700 text-xs">
              {formatearHora(evento.fecha_inicio)} - {formatearHora(evento.fecha_fin)} hrs Uruguay
            </span>
          </div>
        )}
        
        {/* Ubicación */}
        {evento.ubicacion && evento.tipo === 'presencial' && (
          <div className="flex items-center gap-2 text-sm">
            <div className="bg-orange-100 rounded p-1.5">
              <i className="fas fa-map-marker-alt text-orange-600 text-xs"></i>
            </div>
            <span className="font-medium text-gray-700 text-xs">
              {evento.ubicacion}
            </span>
          </div>
        )}
        
        {/* Botón de Zoom - Compacto (solo para eventos online) */}
        {evento.tipo === 'online' && evento.link_zoom ? (
          <Button
            onClick={abrirZoom}
            className="w-full bg-gradient-to-r from-purple-500 to-pink-600 hover:from-purple-600 hover:to-pink-700 text-white font-semibold py-2 text-sm shadow-md hover:shadow-lg transition-all mt-2"
          >
            <i className="fas fa-video mr-1 text-xs"></i>
            Conectar a Zoom
          </Button>
        ) : evento.tipo === 'online' && !evento.link_zoom ? (
          <div className="bg-yellow-50 border border-yellow-300 rounded p-2 mt-2">
            <p className="text-yellow-700 text-xs text-center">
              <i className="fas fa-exclamation-triangle mr-1"></i>
              Sin enlace de Zoom
            </p>
          </div>
        ) : (
          <div className="bg-blue-50 border border-blue-300 rounded p-2 mt-2">
            <p className="text-blue-700 text-xs text-center font-medium">
              <i className="fas fa-map-marker-alt mr-1"></i>
              Evento Presencial
            </p>
          </div>
        )}
      </CardContent>
    </Card>
  );
};

// Componente EstudiantesModal
const EstudiantesModal = ({ isOpen, onClose, clase, estudiantes, loading }) => {
  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-2xl shadow-2xl max-w-3xl w-full max-h-[80vh] overflow-hidden animate-in fade-in zoom-in duration-200">
        {/* Header */}
        <div className="bg-gradient-to-r from-blue-600 to-indigo-600 px-6 py-4 text-white">
          <div className="flex items-center justify-between">
            <div>
              <h3 className="text-2xl font-bold flex items-center gap-2">
                <i className="fas fa-users"></i>
                Estudiantes Inscritos
              </h3>
              <p className="text-blue-100 mt-1 text-sm">
                {clase?.nombre}
              </p>
            </div>
            <button
              onClick={onClose}
              className="text-white hover:bg-white hover:bg-opacity-20 rounded-full p-2 transition-all"
            >
              <i className="fas fa-times text-xl"></i>
            </button>
          </div>
        </div>
        
        {/* Content */}
        <div className="p-6 overflow-y-auto max-h-[calc(80vh-120px)]">
          {loading ? (
            <div className="flex flex-col items-center justify-center py-12">
              <i className="fas fa-spinner fa-spin text-4xl text-blue-600 mb-4"></i>
              <p className="text-gray-600">Cargando estudiantes...</p>
            </div>
          ) : estudiantes.length === 0 ? (
            <div className="flex flex-col items-center justify-center py-12">
              <i className="fas fa-user-slash text-6xl text-gray-300 mb-4"></i>
              <p className="text-gray-500 text-lg">No hay estudiantes inscritos en esta clase</p>
            </div>
          ) : (
            <div className="space-y-3">
              <div className="flex items-center justify-between mb-4 pb-3 border-b-2 border-gray-200">
                <p className="text-sm text-gray-600 font-medium">
                  Total: <span className="text-blue-600 font-bold text-lg">{estudiantes.length}</span> estudiante{estudiantes.length !== 1 ? 's' : ''}
                </p>
              </div>
              
              {estudiantes.map((estudiante, index) => (
                <div
                  key={estudiante.id || index}
                  className="bg-gradient-to-r from-gray-50 to-blue-50 rounded-xl p-4 border border-gray-200 hover:shadow-md transition-all hover:scale-[1.02] cursor-default"
                >
                  <div className="flex items-start gap-4">
                    {/* Avatar */}
                    <div className="flex-shrink-0">
                      <div className="w-12 h-12 bg-gradient-to-br from-blue-500 to-indigo-600 rounded-full flex items-center justify-center text-white font-bold text-lg shadow-lg">
                        {estudiante.nombre?.charAt(0)?.toUpperCase() || 'E'}
                      </div>
                    </div>
                    
                    {/* Info */}
                    <div className="flex-1 min-w-0">
                      <h4 className="font-semibold text-gray-900 text-lg flex items-center gap-2">
                        {estudiante.nombre} {estudiante.apellido}
                        {estudiante.rol === 'estudiante' && (
                          <Badge className="bg-green-100 text-green-700 text-xs">
                            <i className="fas fa-user-graduate mr-1"></i>
                            Estudiante
                          </Badge>
                        )}
                      </h4>
                      <div className="mt-2 space-y-1">
                        <p className="text-sm text-gray-600 flex items-center gap-2">
                          <i className="fas fa-envelope text-blue-600 w-4"></i>
                          <a href={`mailto:${estudiante.email}`} className="hover:text-blue-600 hover:underline">
                            {estudiante.email}
                          </a>
                        </p>
                        {estudiante.telefono && (
                          <p className="text-sm text-gray-600 flex items-center gap-2">
                            <i className="fas fa-phone text-green-600 w-4"></i>
                            {estudiante.telefono}
                          </p>
                        )}
                      </div>
                    </div>
                    
                    {/* Número de orden */}
                    <div className="flex-shrink-0">
                      <div className="bg-blue-100 text-blue-700 rounded-full w-8 h-8 flex items-center justify-center font-bold text-sm">
                        {index + 1}
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
        
        {/* Footer */}
        <div className="bg-gray-50 px-6 py-4 border-t border-gray-200">
          <div className="flex justify-end">
            <Button
              onClick={onClose}
              className="bg-gradient-to-r from-blue-600 to-indigo-600 hover:from-blue-700 hover:to-indigo-700 text-white px-6"
            >
              <i className="fas fa-check mr-2"></i>
              Cerrar
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default MiClaseHoyWidget;
